import React from "react";
import { Box, Typography, Container, Toolbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, Grid, Card, CardContent, Divider, Chip, Button, useMediaQuery, Tabs, Tab, List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import { ExpandMore, Business, MonetizationOn, ShowChart, 
         People, Assignment, ContactMail, Description, 
         LocationOn, Work, Link as LinkIcon } from "@mui/icons-material";

const InvestorRelations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const keyInfo = [
    { icon: <Business />, label: "Mission", value: "Pay, Create, Collaborate" },
    { icon: <ShowChart />, label: "Vision", value: "Payment and Communications technology for creators, contractors, and winners worldwide" },
    { icon: <Description />, label: "Founded", value: "2024" },
    { icon: <LocationOn />, label: "Headquarters", value: "Houston, TX" },
    { icon: <People />, label: "Founder", value: <Link href="https://www.linkedin.com/in/thealexroa" target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}><LinkIcon sx={{ mr: 1 }} />Alex Roa</Link> },
    { icon: <Work />, label: "Team Size", value: "6" },
    { icon: <MonetizationOn />, label: "Funding Stage", value: "SAFE Round" },
  ];

  const products = [
    {
      name: "BlitzPay",
      link: "https://docs.google.com/document/d/1tUpgujF60l8cR9s7pgR8gnmiCKD9lblxVaqKHCN0jVE/edit?usp=sharing",
      features: [
        {
          product: "BlitzPay Financing",
          revenue: "4-10% fee",
          workflow: "Contractor selects BlitzPay, money is issued",
          notes: "Great for large enterprises with complicated vendor payment systems. Ideal for creators looking for fast cash"
        }
      ]
    },
    {
      name: "Blitz Collabs",
      link: "https://www.notion.so/Products-and-Revenue-Streams-208aac23b5fc8093bc0be5752878172a?pvs=21",
      features: [
        {
          product: "Blitz Managed Service Campaign",
          revenue: "20-30% margin",
          workflow: "Totally hands off for client",
          notes: "Good for international, non-technical clients"
        },
        {
          product: "Self-Service Campaign",
          revenue: "10% margin",
          workflow: "More control on output from creators",
          notes: "Best for agencies and technical teams"
        },
        {
          product: "BlitzPay for Collabs",
          revenue: "5-10% margin (additional)",
          workflow: "Micro-financing creator payments",
          notes: "Requires assigned credit line"
        }
      ]
    },
    {
      name: "Blitz Chats",
      link: "https://docs.google.com/document/d/1rols0UXK7ntqFrgNMQR0A3Snmk1As6FtCfL7FYlG5CU/edit?usp=sharing",
      features: [
        {
          product: "Blitz Campaigns Chats",
          revenue: "10-30% margin",
          workflow: "Create up to 5 invoices",
          notes: "Good for micro-creators"
        },
        {
          product: "Blitz Partnerships Chats",
          revenue: "10% margin",
          workflow: "Unlimited invoices, custom branding",
          notes: "Popular among small businesses"
        },
        {
          product: "Blitz Global Chats",
          revenue: "0-10% margin",
          workflow: "Dedicated account manager",
          notes: "For agencies & teams"
        },
        {
          product: "BlitzPay",
          revenue: "4-10% margin",
          workflow: "Micro-financing creator payments",
          notes: "Requires assigned credit line"
        }
      ]
    }
  ];

  const futureStreams = [
    "White-labeling SaaS for other influencer agencies",
    "SaaS on current underdeveloped and non-monetized features",
    "Creator CRM",
    "Brand Deal CRM",
    "Advanced Reporting"
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        width: "100%",
        color: theme.palette.text.primary,
        minHeight: "100vh",
      }}
    >
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 100%)',
        color: '#fff',
        py: 10,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(25, 118, 210, 0.2), transparent 70%)',
          zIndex: 0
        }
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant={isMobile ? "h3" : "h2"} 
            sx={{ 
              fontWeight: 800, 
              mb: 3,
              letterSpacing: '-0.5px'
            }}
          >
            Investor Relations
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 5, 
              maxWidth: 700, 
              mx: 'auto',
              opacity: 0.9,
              lineHeight: 1.7
            }}
          >
            Empowering the future of creator economy through innovative payment and collaboration solutions
          </Typography>
  
        </Container>
      </Box>

      {/* Main Content */}
      <Container sx={{ py: 8, px: { xs: 2, md: 4 } }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{ mb: 4 }}
        >
          <Tab label="Overview" icon={<Business />} />
          <Tab label="Products" icon={<MonetizationOn />} />
          <Tab label="Future" icon={<ShowChart />} />
          <Tab label="Contact" icon={<ContactMail />} />
        </Tabs>

        {tabValue === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                height: '100%'
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3 
                  }}>
                    <Business sx={{ 
                      fontSize: 40, 
                      color: theme.palette.primary.main,
                      mr: 2 
                    }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Company Overview
                    </Typography>
                  </Box>
                  
                  <List disablePadding>
                    {keyInfo.map((item, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 120 }}>
                                {item.label}:
                              </Typography>
                              <Typography variant="body1" sx={{ ml: isMobile ? 0 : 2 }}>
                                {item.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                height: '100%'
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3 
                  }}>
                    <ShowChart sx={{ 
                      fontSize: 40, 
                      color: theme.palette.primary.main,
                      mr: 2 
                    }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Investment Highlights
                    </Typography>
                  </Box>
                  
                  <List disablePadding>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemIcon>
                        <Chip label="1" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Rapidly growing creator economy market" />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemIcon>
                        <Chip label="2" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Multiple revenue streams with high margins" />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemIcon>
                        <Chip label="3" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Proprietary technology stack" />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemIcon>
                        <Chip label="4" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Scalable SaaS business model" />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemIcon>
                        <Chip label="5" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Experienced founding team" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 4 }}>
              Products & Revenue Streams
            </Typography>
            
            {products.map((product, idx) => (
              <Accordion 
                key={idx} 
                defaultExpanded={idx === 0}
                sx={{ 
                  mb: 3, 
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ 
                    backgroundColor: '#fffff',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    minHeight: '72px !important'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MonetizationOn sx={{ 
                      fontSize: 32, 
                      color: theme.palette.primary.dark,
                      mr: 2 
                    }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {product.name}
                    </Typography>
                    <Link 
                      href={product.link} 
                      target="_blank" 
                      rel="noopener" 
                      sx={{ 
                        ml: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: theme.palette.primary.dark
                      }}
                    >
                      <Description sx={{ fontSize: 20, mr: 0.5 }} /> Docs
                    </Link>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead sx={{ 
                        backgroundColor: '#fffff',
                        borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
                      }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Revenue</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Workflow</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {product.features.map((feature, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:nth-of-type(odd)': { backgroundColor: '#fffff' },
                              '&:hover': { backgroundColor: '#fffff' }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{feature.product}</TableCell>
                            <TableCell>
                              <Chip 
                                label={feature.revenue} 
                                color="primary" 
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>{feature.workflow}</TableCell>
                            <TableCell>{feature.notes}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {tabValue === 2 && (
          <Card sx={{ 
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3 
              }}>
                <ShowChart sx={{ 
                  fontSize: 40, 
                  color: theme.palette.primary.main,
                  mr: 2 
                }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Future Revenue Streams
                </Typography>
              </Box>
              
              <List disablePadding>
                {futureStreams.map((item, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                    <ListItemIcon>
                      <Chip 
                        label={index + 1} 
                        color="primary" 
                        sx={{ 
                          backgroundColor: `${theme.palette.primary.main}22`,
                          color: theme.palette.primary.main,
                          fontWeight: 700
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ mt: 4, backgroundColor: '#fffff', p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Growth Projections
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We project a 3x year-over-year growth in revenue for the next 3 years as we expand our product offerings and enter new markets.
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  mt: 3
                }}>
                  <Box sx={{ textAlign: 'center', p: 2, minWidth: 120 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                      3x
                    </Typography>
                    <Typography variant="body2">Year 1 Growth</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 2, minWidth: 120 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                      5x
                    </Typography>
                    <Typography variant="body2">Year 2 Growth</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 2, minWidth: 120 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                      8x
                    </Typography>
                    <Typography variant="body2">Year 3 Growth</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {tabValue === 3 && (
          <Card 
            id="contact"
            sx={{ 
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3 
              }}>
                <ContactMail sx={{ 
                  fontSize: 40, 
                  color: theme.palette.primary.main,
                  mr: 2 
                }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Investor Contact
                </Typography>
              </Box>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: '#fffff', 
                    p: 3, 
                    borderRadius: 3,
                    height: '100%'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Contact Information
                    </Typography>
                    
                    <List disablePadding>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <Assignment sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Link href="mailto:alex@useblitz.co" sx={{ display: 'flex', alignItems: 'center' }}>
                              alex@useblitz.co
                            </Link>
                          } 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <ContactMail sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText primary="+1 917-631-5694" />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <LocationOn sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText primary="Houston, TX" />
                      </ListItem>
                    </List>
                    
                    <Button
                      variant="contained"
                      href="mailto:alex@useblitz.co"
                      sx={{
                        mt: 3,
                        px: 4,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                        '&:hover': { 
                          transform: 'translateY(-3px)',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Email Our Team
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: '#fffff', 
                    p: 3, 
                    borderRadius: 3,
                    height: '100%'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Investor Resources
                    </Typography>
                    
                    <List disablePadding>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <Description sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Link href="#" sx={{ display: 'flex', alignItems: 'center' }}>
                              Investor Presentation Deck
                            </Link>
                          } 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <Description sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Link href="#" sx={{ display: 'flex', alignItems: 'center' }}>
                              Financial Statements
                            </Link>
                          } 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <Description sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Link href="#" sx={{ display: 'flex', alignItems: 'center' }}>
                              Market Analysis Report
                            </Link>
                          } 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <Description sx={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Link href="#" sx={{ display: 'flex', alignItems: 'center' }}>
                              SAFE Agreement Template
                            </Link>
                          } 
                        />
                      </ListItem>
                    </List>
                    
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default InvestorRelations;